// Pure helpers behind "sync ไป platform" on /app-catalog/products/:id/edit and
// behind the "จับคู่หมวด platform" page (mdf-bo-fn#263 / mdf-bo-api#587, spec
// docs/specs/2026-09-22-shopee-master-product-sync-design.md §6B).
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js), so every rule worth testing —
// which platform is offered, how a job step is labelled, which error maps to
// which sentence, what a valid cascader path is — lives here and the .vue files
// only render it. Same split as src/utils/shopeeToSeller.js for Phase 2.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT — read off the handlers, not off the issue text:
// controllers/PlatformAmazePush.go (+ PlatformAmazePushClient.go for the code
// constants), controllers/PlatformCategoryMap.go and
// controllers/PlatformAmazeReference.go, mdf-bo-api#587/#588.
//
//   POST /api/v1/bo/catalog/products/:id/push/:platform_id   {dry_run, overrides}
//   200 {"dry_run":true,"action":"create|update","platform_product_id":"",
//        "payload_summary":{"name_th":"","category_group_id":"","brand_id":"",
//                           "image_count":0,"model_count":0,"weight":0,
//                           "parcel_size":{"width":0,"length":0,"height":0}},
//        "skus":[{"sku_id":0,"sku_code":"","sent_code":"","action":"send|skip",
//                 "reason_code":""}],
//        "rejects":[{"sku_id":0,"sku_code":"","reason_code":"","message":""}],
//        "warnings":[{"code":"","message":""}],
//        "job_id":0}
//
//        A 200 CAN CARRY BOTH a buildable payload AND `rejects`, deliberately
//        (§6B.3 rule 7: send the SKUs that pass, report the ones that do not).
//        So a reject on a 200 is a SKIP NOTICE, not a refusal — the confirm
//        button stays live as long as one SKU says action:"send". Treating the
//        list as fatal is exactly what the first cut of this screen got wrong.
//
//   422 {code:"push_in_flight", message, job_id, status}
//                                — one push per product per platform is already
//                                  queued or running (PlatformAmazePush.go:272).
//                                  THE ONLY in-flight answer: there is no 409
//                                  lane and no job_in_flight / sync_in_flight
//                                  code anywhere in the handler.
//   422 {code:"payload_not_buildable", message, skus, rejects, warnings}
//                                — a structural rule broke (rule 1/10, e.g. the
//                                  cover image). `message` is the only thing
//                                  that says WHICH, so it is always printed.
//   422 {skus, rejects, warnings}  — no code: a product-level reject left
//                                  nothing to push (payload == nil).
//   400 {code:"platform_not_supported"} / {code:"category_not_mapped"}
//   400 {Status,Msg}             — invalid product id / platform_id / body
//   503 {code:"amaze_disabled"|"amaze_not_configured"|"amaze_env_unset"
//             |"amaze_shop_unresolved", message}
//                                — four separate fixes, four sentences
//                                  (PlatformAmazePushClient.go:159-168).
//
//   GET /api/v1/bo/catalog/products/:id/push/:platform_id/status
//   200 {"state","platform_product_id","last_synced_at","last_error",
//        "state_detail","job":{"id","status","step","progress","total","result"}}
//        `state` is the link state, `last_synced_at` is NULL until a push
//        actually finished — which is why the list badge is keyed on the state
//        and not on the existence of the link row.
//   NOT gated on the 503 preconditions: a shop whose credential was just pulled
//   must still be able to read why its last push failed.
//
//   GET /api/platform/v2/category-map?platform_id=11[&category_id=]
//   200 {"maps":[{category_id, category_name, platform_category,
//                 default_attributes, updated_at}]}
//   PUT /api/platform/v2/category-map {platform_id, category_id,
//        platform_category, default_attributes} -> {"saved":true}
//   400 {code:"category_path_mismatch"}  (PlatformCategoryMap.go)
//
//   GET /api/platform/v2/amaze/reference?category_group_id=
//   200 {categories, attributes, brands, shop_addresses, shipping_methods,
//        auto_approve}
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// platforms
// --------------------------------------------------------------------------

// AMAZE_PLATFORM_ID — §6B.2: 11 = Amaze, 10 = Mirakl/Makro. The BE answers 400
// platform_not_supported for anything else on the push route.
export const AMAZE_PLATFORM_ID = 11;
export const MIRAKL_PLATFORM_ID = 10;
export const SHOPEE_PLATFORM_ID = 3;

// PUSH_PLATFORMS is what the modal's platform select offers. The disabled rows
// are listed rather than hidden ON PURPOSE (§6B.8: "ตัวที่ยังไม่รองรับ disable
// พร้อมเหตุผล"): an operator who cannot find Makro at all files a bug, while
// one who sees it greyed out with a reason knows it is coming.
//
// `reason` is an i18n key, or "" when the platform is usable.
export const PUSH_PLATFORMS = [
    { id: AMAZE_PLATFORM_ID, labelKey: "amaze_push.platform.amaze", enabled: true, reason: "" },
    {
        id: MIRAKL_PLATFORM_ID,
        labelKey: "amaze_push.platform.makro",
        enabled: false,
        reason: "amaze_push.platform.disabled_phase4"
    },
    {
        // Shopee is where products come FROM (direction='source'), never a
        // push target — saying so is cheaper than answering the question twice.
        id: SHOPEE_PLATFORM_ID,
        labelKey: "amaze_push.platform.shopee",
        enabled: false,
        reason: "amaze_push.platform.disabled_source_only"
    }
];

// isPushablePlatform: may the confirm button fire for this platform id?
export function isPushablePlatform(platformId) {
    const id = Number(platformId);
    for (const p of PUSH_PLATFORMS) {
        if (p.id === id) return p.enabled === true;
    }
    return false;
}

// platformDisabledReason returns the i18n key for the tooltip on a disabled
// row, or "" when the platform is usable.
export function platformDisabledReason(platformId) {
    const id = Number(platformId);
    for (const p of PUSH_PLATFORMS) {
        if (p.id === id) return p.enabled ? "" : p.reason;
    }
    return "amaze_push.platform.disabled_unknown";
}

// --------------------------------------------------------------------------
// link state  (mdfpf_product_platform_link.state, §6B.2 state machine)
// --------------------------------------------------------------------------

// PUSH_STATES — the whole vocabulary, in the order of the state machine:
//   pushing   the job is running (images -> create -> link)
//   reviewing created on Amaze, waiting for their review
//   live      Amaze approved it / auto_approve was on
//   rejected  Amaze refused it; state_detail carries their message
//   failed    a step of OUR job blew up; last_error carries why; re-pushable
export const PUSH_STATES = ["pushing", "reviewing", "live", "rejected", "failed"];

// PUSH_STATE_TAG_TYPES maps a state to an el-tag `type`. Element UI 2.4.11 has
// success / info / warning / danger plus the default.
export const PUSH_STATE_TAG_TYPES = {
    pushing: "info",
    reviewing: "warning",
    live: "success",
    rejected: "danger",
    failed: "danger"
};

export function pushStateTagType(state) {
    return PUSH_STATE_TAG_TYPES[normaliseString(state)] || "info";
}

// pushStateI18nKey — an unrecognised state gets `amaze_push.state.unknown`
// rather than being printed raw, the same fallback shopeeToSeller uses.
export function pushStateI18nKey(state) {
    const s = normaliseString(state);
    if (s === "") return "amaze_push.state.none";
    if (PUSH_STATES.indexOf(s) === -1) return "amaze_push.state.unknown";
    return "amaze_push.state." + s;
}

// isPushInFlight: polling continues while the state is `pushing` — §6B.8 step 3
// says poll "until state ∉ {pushing}".
export function isPushInFlight(state) {
    return normaliseString(state) === "pushing";
}

// isPushFailed: the result block shows last_error + a "ลองใหม่" button.
export function isPushFailed(state) {
    return normaliseString(state) === "failed";
}

// --------------------------------------------------------------------------
// job steps  (mdfpf_push_job.step, §6B.5)
// --------------------------------------------------------------------------

// PUSH_JOB_STEPS in execution order. The UI shows them as the breadcrumb
// §6B.8 asks for: อัปรูป → สร้าง → รอรีวิว. `link` has no separate label — it
// is the tail of `create` from the operator's point of view (upsert the SKU
// links right after the product exists) and a fourth chip for a step that
// lasts milliseconds would only add noise.
export const PUSH_JOB_STEPS = ["images", "create", "link", "poll"];

// PUSH_STEP_LABEL_STEPS are the three the progress bar renders.
export const PUSH_STEP_LABEL_STEPS = ["images", "create", "poll"];

export function pushStepI18nKey(step) {
    const s = normaliseString(step);
    if (s === "") return "amaze_push.step.queued";
    if (PUSH_JOB_STEPS.indexOf(s) === -1) return "amaze_push.step.unknown";
    return "amaze_push.step." + s;
}

// pushStepIndex places a step on the three-chip bar: `link` shares the `create`
// chip (see above), an unknown or empty step is -1 = "nothing lit yet".
export function pushStepIndex(step) {
    const s = normaliseString(step);
    if (s === "link") return PUSH_STEP_LABEL_STEPS.indexOf("create");
    return PUSH_STEP_LABEL_STEPS.indexOf(s);
}

// pushJobProgressPercent turns {progress, total} into 0..100 for el-progress.
// total 0 (the job has not counted its work yet) is 0%, never NaN and never a
// division by zero, and the result is clamped because a retry can report a
// progress above the total it recorded on the previous attempt.
export function pushJobProgressPercent(job) {
    if (!job || typeof job !== "object") return 0;
    const total = Number(job.total);
    const progress = Number(job.progress);
    if (!isFinite(total) || total <= 0) return 0;
    if (!isFinite(progress) || progress <= 0) return 0;
    const pct = Math.round((progress / total) * 100);
    if (pct < 0) return 0;
    if (pct > 100) return 100;
    return pct;
}

// POLL_INTERVAL_MS — §6B.8 step 3: "poll …/status every 2s".
export const POLL_INTERVAL_MS = 2000;

// --------------------------------------------------------------------------
// rejects (§6B.3 — a CLOSED set; adding one means editing the spec)
// --------------------------------------------------------------------------

//   sku_code_too_long_for_platform  the code is longer than the 100-rune limit
//                                   we set ourselves (Amaze publishes none)
//   sku_code_invalid_chars          control chars / newline / surrounding space
//   category_not_mapped             the product's seller category has no row in
//                                   mdfpf_category_platform_map — product-level
//   missing_required_attribute      an attribute the tree marks mandatory has
//                                   no value in defaults or overrides — product-level
//   no_active_sku                   nothing was left to send after per-SKU rejects
export const REJECT_REASON_CODES = [
    "sku_code_too_long_for_platform",
    "sku_code_invalid_chars",
    "category_not_mapped",
    "missing_required_attribute",
    "no_active_sku"
];

// PRODUCT_LEVEL_REJECT_CODES arrive with sku_id 0: they are about the product,
// so the modal must not print "(SKU: 0)" after them.
export const PRODUCT_LEVEL_REJECT_CODES = [
    "category_not_mapped",
    "missing_required_attribute",
    "no_active_sku"
];

export function rejectReasonI18nKey(code) {
    const c = normaliseString(code);
    if (REJECT_REASON_CODES.indexOf(c) === -1) return "amaze_push.reject.unknown";
    return "amaze_push.reject." + c;
}

// rejectDisplayText is the LINE the modal prints for one reject.
//
// A known code is translated. An UNKNOWN code — the BE growing a sixth reason
// before this screen learns about it — falls back to the backend's own
// `message`, so the operator reads the real reason instead of a generic
// sentence or a blank bullet. `translate` is optional so the function is
// testable without a Vue instance.
export function rejectDisplayText(reject, translate) {
    const t = typeof translate === "function" ? translate : (k) => k;
    const code = fieldOf(reject, "reason_code", "ReasonCode");
    if (REJECT_REASON_CODES.indexOf(code) !== -1) return t("amaze_push.reject." + code);
    const message = fieldOf(reject, "message", "Message");
    if (message !== "") return message;
    return t("amaze_push.reject.unknown");
}

// rejectDetailText is the grey suffix: the backend's message, but only for a
// code we translated ourselves — repeating it under an unknown code, where
// rejectDisplayText already printed it, reads as a stutter.
export function rejectDetailText(reject) {
    const code = fieldOf(reject, "reason_code", "ReasonCode");
    if (REJECT_REASON_CODES.indexOf(code) === -1) return "";
    return fieldOf(reject, "message", "Message");
}

// isProductLevelReject: true when the reject is about the product, not a SKU.
export function isProductLevelReject(reject) {
    const code = fieldOf(reject, "reason_code", "ReasonCode");
    if (PRODUCT_LEVEL_REJECT_CODES.indexOf(code) !== -1) return true;
    return Number(fieldOf(reject, "sku_id", "SKUID")) === 0;
}

// --------------------------------------------------------------------------
// warnings (§6B.3)
// --------------------------------------------------------------------------

//   image_not_uploaded   a gallery image has no Amaze asset yet and was skipped
//                        (the COVER failing is an error, not a warning)
//   sku_skipped          an inactive SKU was left out
//   attribute_defaulted  an attribute fell back to the category default
export const WARNING_CODES = ["image_not_uploaded", "sku_skipped", "attribute_defaulted"];

export function warningI18nKey(code) {
    const c = normaliseString(code);
    if (WARNING_CODES.indexOf(c) === -1) return "amaze_push.warning.unknown";
    return "amaze_push.warning." + c;
}

export function warningDisplayText(warning, translate) {
    const t = typeof translate === "function" ? translate : (k) => k;
    const code = fieldOf(warning, "code", "Code");
    if (WARNING_CODES.indexOf(code) !== -1) return t("amaze_push.warning." + code);
    const message = fieldOf(warning, "message", "Message");
    return message !== "" ? message : t("amaze_push.warning.unknown");
}

// pushWarnings pulls `warnings` off a push response, dropping anything that is
// not an object so v-for cannot render `undefined`.
export function pushWarnings(data) {
    return arrayField(data, "warnings", "Warnings");
}

// pushRejects pulls `rejects` off a 200 body (a dry run reports them without
// failing when some SKUs still go through).
export function pushRejects(data) {
    return arrayField(data, "rejects", "Rejects");
}

// rejectsFromError pulls `rejects` off a 422 body.
export function rejectsFromError(error) {
    return arrayField(responseData(error), "rejects", "Rejects");
}

// --------------------------------------------------------------------------
// per-SKU rows (ส่ง / ข้าม)
// --------------------------------------------------------------------------

// SKU_ACTION_TAG_TYPES: send -> success, skip -> info. Anything else is info.
export const SKU_ACTION_TAG_TYPES = { send: "success", skip: "info" };

export function skuActionTagType(action) {
    return SKU_ACTION_TAG_TYPES[normaliseString(action)] || "info";
}

export function skuActionI18nKey(action) {
    const a = normaliseString(action);
    if (!Object.prototype.hasOwnProperty.call(SKU_ACTION_TAG_TYPES, a)) {
        return "amaze_push.sku_action.unknown";
    }
    return "amaze_push.sku_action." + a;
}

// pushSkuRows normalises `skus` for the table.
//
// `sent_code` is the code ACTUALLY sent to Amaze, which §6B.2 keeps separate
// from the seller's `sku_code` because the platform may need an escaped form.
// When they differ the operator has to see both — that pair is the only thing
// that explains an order coming back under a code nobody recognises — so the
// row carries a `code_differs` flag instead of silently showing one of them.
export function pushSkuRows(data) {
    const rows = arrayField(data, "skus", "SKUs");
    return rows.map((row) => {
        const skuCode = fieldOf(row, "sku_code", "SKUCode");
        const sentCode = fieldOf(row, "sent_code", "SentCode");
        return {
            sku_id: Number(fieldOf(row, "sku_id", "SKUID")) || 0,
            sku_code: skuCode,
            sent_code: sentCode,
            code_differs: sentCode !== "" && sentCode !== skuCode,
            action: normaliseString(fieldOf(row, "action", "Action")),
            reason_code: fieldOf(row, "reason_code", "ReasonCode")
        };
    });
}

// skuRowReasonText: why a SKU is skipped. Uses the reject vocabulary, since the
// BE reuses the same codes in the per-SKU `reason_code`.
export function skuRowReasonText(row, translate) {
    const code = fieldOf(row, "reason_code", "ReasonCode");
    if (code === "") return "";
    const t = typeof translate === "function" ? translate : (k) => k;
    return t(rejectReasonI18nKey(code));
}

// --------------------------------------------------------------------------
// payload summary
// --------------------------------------------------------------------------

// payloadSummary normalises `payload_summary` so the template can bind fields
// without a chain of v-if guards. Missing numbers become 0, missing strings "".
export function payloadSummary(data) {
    const raw = (data && (data.payload_summary || data.PayloadSummary)) || {};
    const parcel = raw.parcel_size || raw.ParcelSize || {};
    return {
        name_th: stringOf(raw.name_th),
        category_group_id: stringOf(raw.category_group_id),
        brand_id: stringOf(raw.brand_id),
        image_count: Number(raw.image_count) || 0,
        model_count: Number(raw.model_count) || 0,
        weight: Number(raw.weight) || 0,
        parcel_size: {
            width: Number(parcel.width) || 0,
            length: Number(parcel.length) || 0,
            height: Number(parcel.height) || 0
        }
    };
}

// --------------------------------------------------------------------------
// request bodies
// --------------------------------------------------------------------------

// pushBody builds the POST body.
//
// `dry_run` is explicit at every call site on purpose — "ตรวจสอบ" must send
// true before "ยืนยัน sync" may send false, and a spec asserts that ordering.
// It is a BOOLEAN, not a string: the Go handler binds it into a bool.
//
// `overrides` is always an object, never null: §6B.3 merges it over the
// category defaults at the top level, and a null would make the BE's own
// unmarshal of `map[string]any` ambiguous.
export function pushBody(dryRun, overrides) {
    return {
        dry_run: dryRun === true,
        overrides: overrides && typeof overrides === "object" && !Array.isArray(overrides) ? overrides : {}
    };
}

// categoryMapBody builds the PUT /category-map body.
//
// platform_id and category_id are NUMBERS. The Go handler binds them into
// `int` / `int64` and answers 400 "invalid request body" to a JSON string —
// the exact mistake the Shopee modal made in the other direction, where
// platform_shop_id had to be a string.
export function categoryMapBody(platformId, categoryId, categoryPath, defaultAttributes) {
    const path = Array.isArray(categoryPath) ? categoryPath.map(stringOf).filter((v) => v !== "") : [];
    return {
        platform_id: Number(platformId) || 0,
        category_id: Number(categoryId) || 0,
        platform_category: {
            // The LAST element of the path, which §6B.2 requires to equal
            // category_group_id. Deriving it here rather than taking it as an
            // argument is what makes it impossible for the page to send a pair
            // the BE will reject with 400 category_path_mismatch.
            category_group_id: path.length ? path[path.length - 1] : "",
            category_path: path
        },
        default_attributes:
            defaultAttributes && typeof defaultAttributes === "object" && !Array.isArray(defaultAttributes)
                ? defaultAttributes
                : {}
    };
}

// isValidCascaderPath: a path is usable when it has at least one element and
// every element is a non-empty string. The el-cascader hands back [] while the
// operator is still choosing, and a half-chosen path must not enable Save.
export function isValidCascaderPath(categoryPath) {
    if (!Array.isArray(categoryPath) || categoryPath.length === 0) return false;
    for (const v of categoryPath) {
        if (stringOf(v) === "") return false;
    }
    return true;
}

// categoryGroupIdFromPath is the leaf — the value that goes into
// category_group_id. §6B.2 / testdata README trap 2: category_path INCLUDES
// the leaf, unlike a category object's own parent_path, which excludes it.
export function categoryGroupIdFromPath(categoryPath) {
    if (!isValidCascaderPath(categoryPath)) return "";
    return stringOf(categoryPath[categoryPath.length - 1]);
}

// --------------------------------------------------------------------------
// reference -> UI structures
// --------------------------------------------------------------------------

// categoriesToCascaderOptions turns GET /amaze/reference's nested `categories`
// into el-cascader options {value,label,children}.
//
// A leaf is emitted WITHOUT a `children` key at all, not with `children: []`:
// Element UI 2.4.11 renders an empty array as an expandable node with an empty
// panel, i.e. a category that looks broken and cannot be selected.
export function categoriesToCascaderOptions(categories) {
    if (!Array.isArray(categories)) return [];
    return categories.map((cat) => {
        const node = {
            value: stringOf(cat && cat.id),
            label: stringOf((cat && (cat.title || cat.name)) || "")
        };
        const children = categoriesToCascaderOptions(cat && cat.childrens);
        if (children.length) node.children = children;
        return node;
    });
}

// mandatoryAttributes filters the attribute tree down to the inputs the
// mapping page MUST collect. §6B.3 rule 6 turns a missing one into a
// product-level `missing_required_attribute` reject at push time, so the page
// is the last chance to ask.
export function mandatoryAttributes(attributes) {
    if (!Array.isArray(attributes)) return [];
    return attributes.filter((a) => a && a.mandatory === true);
}

// attributeOptions lists one attribute's selectable values for an el-select.
export function attributeOptions(attribute) {
    const children = (attribute && attribute.childrens) || [];
    if (!Array.isArray(children)) return [];
    return children.map((c) => ({
        value: stringOf(c && c.id),
        label: stringOf((c && (c.name || c.value)) || "")
    }));
}

// buildDefaultAttributes assembles the default_attributes block §6B.2 pins.
//
// EVERY value here is a platform id chosen by the operator. §6B.2 is explicit
// that none of them may be hard-coded ("ค่าทุกตัวเป็น id ของแพลตฟอร์ม ห้าม
// hard-code ในโค้ด"), which is why this takes them all as arguments and
// invents nothing but the structure.
//
// `shipping` is rebuilt from the shipping_methods row the operator picked, so
// the nested shipping_channel block matches what the shop actually has.
// `payment_configs` is passed through untouched: §6B.2's provenance table says
// there is NO endpoint that lists payment channels, so its only source is a
// value seeded by the §6B.6 runbook — inventing one here would be a fabricated id.
export function buildDefaultAttributes(input) {
    const src = input && typeof input === "object" ? input : {};
    const shippingMethod = src.shipping_method || null;
    const shipping = [];
    if (shippingMethod && stringOf(shippingMethod.shop_shipping_method_id) !== "") {
        shipping.push({
            shop_shipping_method_id: stringOf(shippingMethod.shop_shipping_method_id),
            shipping_channel_id: stringOf(shippingMethod.shipping_channel_id),
            enable: true,
            shipping_fee: Number(src.shipping_fee) || 0,
            shipping_channel: {
                id: stringOf(shippingMethod.shipping_channel_id),
                code_name: stringOf(shippingMethod.code_name),
                is_default: shippingMethod.is_default === true,
                name: stringOf(shippingMethod.name),
                seller_editable: shippingMethod.seller_editable === true,
                shipping_fee_editable: shippingMethod.shipping_fee_editable === true
            }
        });
    }

    const attributes = [];
    const chosen = src.attributes && typeof src.attributes === "object" ? src.attributes : {};
    for (const attributeId of Object.keys(chosen)) {
        const optionIds = toStringArray(chosen[attributeId].option_ids);
        const values = toStringArray(chosen[attributeId].value);
        if (optionIds.length === 0 && values.length === 0) continue;
        attributes.push({
            attribute_id: stringOf(attributeId),
            value: values,
            attribute_option_ids: optionIds
        });
    }

    return {
        brand_id: stringOf(src.brand_id),
        attributes: attributes,
        shipping: shipping,
        payment_configs: Array.isArray(src.payment_configs) ? src.payment_configs : [],
        installment_configuration: { is_active: false },
        inventory_shop_address_id: stringOf(src.inventory_shop_address_id),
        inventory_warehouse_name: stringOf(src.inventory_warehouse_name),
        condition: "new",
        product_type: "physical_product",
        pre_order: false,
        pre_order_days: 0,
        parcel_size: {
            width: Number(src.parcel_width) || 0,
            length: Number(src.parcel_length) || 0,
            height: Number(src.parcel_height) || 0
        },
        weight: Number(src.weight) || 0,
        // §6B.9 item 6: what update_flag[] means at create time is UNVERIFIED;
        // the spec says to use the vendor example's value until Amaze answers.
        update_flag: ["attributes", "variant", "variant_option"]
    };
}

// --------------------------------------------------------------------------
// product list badges (§6B.8)
// --------------------------------------------------------------------------

// platformLinkFor picks one platform's link off a product-list row.
//
// The list carries `platform_links` only when it was asked for it — GET
// /api/v1/bo/catalog/products?with_platform_links=1 (mdf-bo-api#588). A row
// without the field simply has no badge; it must never throw.
export function platformLinkFor(row, platformId) {
    const links = arrayField(row, "platform_links", "PlatformLinks");
    const id = Number(platformId);
    for (const link of links) {
        if (Number(link.platform_id) === id) return link;
    }
    return null;
}

// amazeLinkFor is platformLinkFor bound to Amaze — what both badges read.
export function amazeLinkFor(row) {
    return platformLinkFor(row, AMAZE_PLATFORM_ID);
}

// isEditedSinceSync — the "แก้ไขหลัง sync ล่าสุด" badge. §6B.2 defines it as
// `mdfpf_product.updated_at > link.last_synced_at`.
//
// FALSE, not true, when either timestamp is missing or unparseable. A product
// that has never been synced has no badge at all (there is nothing to be newer
// than), and an unparseable date must not light a warning the operator cannot
// act on.
export function isEditedSinceSync(row, link) {
    const target = link || amazeLinkFor(row);
    if (!target) return false;
    const synced = parseTime(target.last_synced_at || target.LastSyncedAt);
    if (synced === null) return false;
    const updated = parseTime(row && (row.updated_at || row.UpdatedAt));
    if (updated === null) return false;
    return updated > synced;
}

// hasAmazeLink: is there an Amaze link row at all? (The BADGE is keyed on the
// link's STATE — see amazeBadge — not on this.)
export function hasAmazeLink(row) {
    return amazeLinkFor(row) !== null;
}

// amazeLinkState is the Amaze link's `state`, normalised, or "" when the row
// carries no link (or a link that has never been given a state).
export function amazeLinkState(row, link) {
    const target = link || amazeLinkFor(row);
    if (!target) return "";
    return normaliseString(target.state !== undefined ? target.state : target.State);
}

// AMAZE_SYNCED_STATES — the only two states that mean "Amaze has this product".
//
//   reviewing  it exists on Amaze and is queued for their review
//   live       Amaze approved it / auto_approve was on
//
// `pushing` is a job in flight, `failed` is OUR job blowing up and `rejected`
// is Amaze refusing the product. None of those is a sync, and painting them
// green — which the first cut did, because it only checked that a link row
// existed — tells the operator a product is published when it is not.
export const AMAZE_SYNCED_STATES = ["reviewing", "live"];

export function isAmazeSyncedState(state) {
    return AMAZE_SYNCED_STATES.indexOf(normaliseString(state)) !== -1;
}

// amazeBadgeTagType is the list badge's colour. Deliberately NOT
// pushStateTagType: in the modal a `reviewing` product is a warning (the
// operator is waiting on someone else), while in the list it is a success (the
// push worked — the wait is normal). Only these two say green.
export function amazeBadgeTagType(state) {
    const s = normaliseString(state);
    if (isAmazeSyncedState(s)) return "success";
    if (s === "failed" || s === "rejected") return "danger";
    return "info";
}

// amazeBadge describes the one tag the product list renders, or null when the
// row has no Amaze link (a list not asked for ?with_platform_links=1 has none,
// and must simply show nothing).
//
//   type       the el-tag type: success only for reviewing/live
//   state_key  the i18n key of the state WORD, so "failed" is readable rather
//              than implied by a colour
//   synced     may the "· <เวลา>" suffix be printed? Only when the state is a
//              real sync AND last_synced_at actually has a value — the BE
//              leaves it NULL until a push finishes.
export function amazeBadge(row) {
    const link = amazeLinkFor(row);
    if (!link) return null;
    const state = amazeLinkState(row, link);
    const syncedAt = parseTime(link.last_synced_at || link.LastSyncedAt);
    return {
        state: state,
        type: amazeBadgeTagType(state),
        state_key: pushStateI18nKey(state),
        synced: isAmazeSyncedState(state) && syncedAt !== null
    };
}

// --------------------------------------------------------------------------
// errors
// --------------------------------------------------------------------------

// AMAZE_UNAVAILABLE_CODES are the four 503 codes §6B.4 insists stay distinct,
// because "switched off" and "no credentials" looking identical is what sent an
// operator hunting through the wrong config file on the wrong host. Each has a
// different fix (PlatformAmazePushClient.go:170-234):
//   amaze_disabled        platform.amaze.enabled = true
//   amaze_not_configured  put username/password in THIS container's config
//   amaze_env_unset       platform.amaze.env = prod (UAT answers 503 on every path)
//   amaze_shop_unresolved platform.amaze.shop_id, or press ทดสอบการเชื่อมต่อ once
export const AMAZE_UNAVAILABLE_CODES = [
    "amaze_not_configured",
    "amaze_disabled",
    "amaze_env_unset",
    "amaze_shop_unresolved"
];

// IN_FLIGHT_CODE — the one code the handler sends (PlatformAmazePush.go:273).
// The 409 lane and the job_in_flight / sync_in_flight codes this file used to
// accept do not exist in any handler; handling bodies nobody can send hides
// which branch is the live one.
export const IN_FLIGHT_CODE = "push_in_flight";

// PAYLOAD_NOT_BUILDABLE_CODE — PlatformAmazePush.go:214.
export const PAYLOAD_NOT_BUILDABLE_CODE = "payload_not_buildable";

// pushErrorI18nKey maps an axios error to the sentence the modal shows.
//   422 push_in_flight        -> in_flight (+ pushInFlightJobId re-attaches)
//   422 payload_not_buildable -> payload_not_buildable (+ the BE message)
//   422 other                 -> rejected; the reject list is rendered with it
//   400 category_not_mapped   -> link to the mapping page
//   400 platform_not_supported-> platform_not_supported
//   400 other                 -> bad_request
//   503 one of the four codes -> that code's own sentence
//   502                       -> upstream
//   everything else (incl. 500, and a 409 from a proxy) -> generic
export function pushErrorI18nKey(error) {
    const status = responseStatus(error);
    const data = responseData(error);
    const code = errorBodyCode(data);

    if (status === 422) {
        if (code === IN_FLIGHT_CODE) return "amaze_push.error.in_flight";
        if (code === PAYLOAD_NOT_BUILDABLE_CODE) return "amaze_push.error.payload_not_buildable";
        return "amaze_push.error.rejected";
    }
    if (status === 400) {
        if (code === "category_not_mapped") return "amaze_push.error.category_not_mapped";
        if (code === "platform_not_supported") return "amaze_push.error.platform_not_supported";
        return "amaze_push.error.bad_request";
    }
    if (status === 503) {
        if (AMAZE_UNAVAILABLE_CODES.indexOf(code) !== -1) return "amaze_push.error." + code;
        return "amaze_push.error.unavailable";
    }
    if (status === 502) return "amaze_push.error.upstream";
    return "amaze_push.error.generic";
}

// isCategoryNotMapped drives the "ไปหน้าจับคู่หมวด" link in the error block —
// the one error the operator can fix themselves, in one click.
export function isCategoryNotMapped(error) {
    return pushErrorI18nKey(error) === "amaze_push.error.category_not_mapped";
}

// isPayloadNotBuildable: a 422 the modal must NOT fold into the plain reject
// list — its body carries `rejects` too, but the reason the push is impossible
// is in `message`.
export function isPayloadNotBuildable(error) {
    return (
        responseStatus(error) === 422 && errorBodyCode(responseData(error)) === PAYLOAD_NOT_BUILDABLE_CODE
    );
}

// pushInFlightJobId digs the running job's id out of either in-flight shape, so
// the modal can re-attach to it instead of starting a second push. 0 when the
// body carries none.
export function pushInFlightJobId(error) {
    const data = responseData(error);
    if (!data || typeof data !== "object") return 0;
    const raw = data.job_id !== undefined ? data.job_id : data.JobID !== undefined ? data.JobID : (data.job || {}).id;
    const id = Number(raw);
    return isFinite(id) && id > 0 ? id : 0;
}

// pushErrorDetail is the backend's own text, printed under the translated
// sentence.
//
// `message` WINS, code or no code. Every coded body this handler sends carries
// a message that is strictly more specific than the translation can be — the
// env value it actually found, the attribute that is missing, the shop it could
// not resolve, the structural rule that broke on payload_not_buildable — and
// suppressing it (as this function used to, whenever a `code` was present) threw
// away the one line that tells the operator what to change.
export function pushErrorDetail(error) {
    const data = responseData(error);
    if (!data || typeof data !== "object") return "";
    const message = stringOf(data.message || data.Message);
    if (message !== "") return message;
    return stringOf(data.Msg || data.msg || data.Cause || data.cause);
}

// categoryMapErrorI18nKey is the same job for the mapping page's PUT.
//   400 category_path_mismatch  -> the path/leaf rule, spelled out
//   400 platform_not_supported  -> platform_not_supported
//   400 other                   -> bad_request
//   everything else             -> generic
export function categoryMapErrorI18nKey(error) {
    const status = responseStatus(error);
    const code = errorBodyCode(responseData(error));
    if (status === 400) {
        if (code === "category_path_mismatch") return "amaze_push.map.error.path_mismatch";
        if (code === "platform_not_supported") return "amaze_push.map.error.platform_not_supported";
        return "amaze_push.map.error.bad_request";
    }
    if (status === 503) {
        if (AMAZE_UNAVAILABLE_CODES.indexOf(code) !== -1) return "amaze_push.error." + code;
        return "amaze_push.error.unavailable";
    }
    return "amaze_push.map.error.generic";
}

// indexCategoryMaps folds GET /category-map's `maps` into a lookup by
// category_id (a number key read back as a number), so the mapping table can
// render a row per seller category and highlight the unmapped ones.
export function indexCategoryMaps(payload) {
    const list = arrayField(payload, "maps", "Maps");
    const index = {};
    for (const row of list) {
        const id = Number(row.category_id !== undefined ? row.category_id : row.CategoryID);
        if (!isFinite(id) || id <= 0) continue;
        index[id] = {
            category_id: id,
            category_name: stringOf(row.category_name),
            platform_category: row.platform_category || {},
            default_attributes: row.default_attributes || {},
            updated_at: stringOf(row.updated_at)
        };
    }
    return index;
}

// isCategoryMapped: does this seller category have a mapping for the platform
// currently being edited? The page paints the unmapped rows (§6B.8: "แถวที่ยัง
// ไม่จับคู่ขึ้นสีเตือน").
export function isCategoryMapped(index, categoryId) {
    if (!index || typeof index !== "object") return false;
    const id = Number(categoryId);
    if (!isFinite(id) || id <= 0) return false;
    const row = index[id];
    if (!row) return false;
    return stringOf((row.platform_category || {}).category_group_id) !== "";
}

// --------------------------------------------------------------------------
// small shared helpers
// --------------------------------------------------------------------------

function normaliseString(value) {
    return value === null || value === undefined ? "" : String(value).trim();
}

function stringOf(value) {
    return normaliseString(value);
}

// fieldOf reads a field under its snake_case name, falling back to the Go
// struct's exported name — a body serialised without json tags (which a
// hand-rolled test double or a future handler can produce) then still renders.
function fieldOf(obj, snake, pascal) {
    if (!obj || typeof obj !== "object") return "";
    const raw = obj[snake] !== undefined && obj[snake] !== null ? obj[snake] : obj[pascal];
    return normaliseString(raw);
}

function arrayField(obj, snake, pascal) {
    if (!obj || typeof obj !== "object") return [];
    const list = obj[snake] !== undefined && obj[snake] !== null ? obj[snake] : obj[pascal];
    if (!Array.isArray(list)) return [];
    return list.filter((v) => v && typeof v === "object");
}

function toStringArray(value) {
    if (value === null || value === undefined) return [];
    const list = Array.isArray(value) ? value : [value];
    return list.map(stringOf).filter((v) => v !== "");
}

function responseStatus(error) {
    return error && error.response ? Number(error.response.status) || 0 : 0;
}

function responseData(error) {
    return error && error.response ? error.response.data : null;
}

// errorBodyCode reads the machine-readable code. jsonErrWithCode
// (controllers/Member_address.go:59) puts it in `code`; Code / error_code are
// tolerated for a proxy that re-cases the field.
function errorBodyCode(data) {
    if (!data || typeof data !== "object") return "";
    return normaliseString(data.code || data.Code || data.error_code || data.errorCode);
}

// parseTime returns epoch millis, or null when the value is absent or not a
// date. Never NaN: every caller compares the result, and NaN comparisons are
// silently false in both directions, which would make a badge flicker rather
// than fail.
function parseTime(value) {
    const s = normaliseString(value);
    if (s === "") return null;
    const ms = Date.parse(s);
    return isFinite(ms) ? ms : null;
}
